import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useDebouncedValue } from "@mantine/hooks";
import { Spotlight } from "@mantine/spotlight";
import { Group, Loader, Text } from "@mantine/core";
import {
  IconKey,
  IconLayoutDashboard,
  IconLogs,
  IconSearch,
  IconSettings,
  IconUserCircle,
  IconUser,
  IconUsers,
} from "@tabler/icons-react";
import { hasAtLeast, rolePrivilege } from "@wwrp/shared";
import type { Member } from "@wwrp/shared";
import { useAuth } from "../contexts/AuthContext.js";
import { getAccessRole } from "../api/auth.js";
import { membersApi } from "../api/members.js";
import { ROLE_LABEL } from "./RoleBadge.js";

interface NavItem {
  id: string;
  label: string;
  description: string;
  keywords: string[];
  icon: React.ReactNode;
  path: string;
}

/** Returns true if the member's community status is considered active. */
function isActiveMember(m: Member): boolean {
  const s = m.communityStatus?.toLowerCase() ?? "";
  return s === "active" || s === "on_loa";
}

/** Sort spotlight member results: active first, then by role privilege desc, then name asc. */
function sortSpotlightMembers(items: Member[]): Member[] {
  return [...items].sort((a, b) => {
    const aActive = isActiveMember(a) ? 0 : 1;
    const bActive = isActiveMember(b) ? 0 : 1;
    if (aActive !== bActive) return aActive - bActive;
    const roleDiff = rolePrivilege(b.role) - rolePrivilege(a.role);
    if (roleDiff !== 0) return roleDiff;
    return (a.firstNameLastInitial ?? "").localeCompare(b.firstNameLastInitial ?? "");
  });
}

export function AppSpotlight() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const role = getAccessRole(user);
  const [query, setQuery] = useState("");
  const [debouncedQuery] = useDebouncedValue(query, 250);

  const isStaff = role ? hasAtLeast(role, "staff_in_training") : false;

  // Build nav items filtered by current user's role
  const navItems = useMemo<NavItem[]>(() => {
    const items: NavItem[] = [
      {
        id: "dashboard",
        label: "Dashboard",
        description: "Announcements, community stats, and who is online",
        keywords: ["home", "announcements", "overview", "stats", "online"],
        icon: <IconLayoutDashboard size={18} stroke={1.5} />,
        path: "/",
      },
      {
        id: "my-profile",
        label: "My Profile",
        description: "Your profile, records, and LOA requests",
        keywords: ["me", "profile", "loa", "conduct", "records"],
        icon: <IconUserCircle size={18} stroke={1.5} />,
        path: "/my-profile",
      },
      {
        id: "settings",
        label: "Settings",
        description: "Manage your account and notification preferences",
        keywords: ["preferences", "notifications", "discord", "account"],
        icon: <IconSettings size={18} stroke={1.5} />,
        path: "/settings",
      },
    ];

    if (role && hasAtLeast(role, "staff_in_training")) {
      items.push({
        id: "members",
        label: "Members",
        description: "Search and view all member profiles",
        keywords: ["search", "list", "staff", "profiles"],
        icon: <IconUsers size={18} stroke={1.5} />,
        path: "/members",
      });
    }

    if (role && hasAtLeast(role, "administration")) {
      items.push({
        id: "audit-log",
        label: "Audit Log",
        description: "Review all staff actions across the platform",
        keywords: ["logs", "history", "activity", "actions"],
        icon: <IconLogs size={18} stroke={1.5} />,
        path: "/admin/audit-log",
      });
    }

    if (role && hasAtLeast(role, "head_administration")) {
      items.push({
        id: "role-mappings",
        label: "Role Mappings",
        description: "Map Discord roles to Portal permission roles",
        keywords: ["discord", "permissions", "roles", "mapping"],
        icon: <IconKey size={18} stroke={1.5} />,
        path: "/role-mappings",
      });
    }

    return items;
  }, [role]);

  // Client-side filter for nav items
  const filteredNav = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return navItems;
    return navItems.filter(
      (item) =>
        item.label.toLowerCase().includes(q) ||
        item.description.toLowerCase().includes(q) ||
        item.keywords.some((k) => k.includes(q)),
    );
  }, [navItems, query]);

  const trimmed = debouncedQuery.trim();
  const canSearchMembers = isStaff && trimmed.length >= 2;

  const { data: memberResults, isFetching } = useQuery({
    queryKey: ["spotlight-members", trimmed],
    queryFn: () => membersApi.list({ q: trimmed, limit: 10 }),
    enabled: canSearchMembers,
    staleTime: 15_000,
  });

  const members = useMemo(
    () => sortSpotlightMembers(memberResults?.items ?? []).slice(0, 8),
    [memberResults],
  );
  const showMemberGroup = canSearchMembers && members.length > 0;
  const isEmpty = filteredNav.length === 0 && members.length === 0 && !isFetching;

  return (
    <Spotlight.Root
      query={query}
      onQueryChange={setQuery}
      shortcut={["mod+K", "mod+P", "mod+L"]}
      clearQueryOnClose
    >
      <Spotlight.Search
        placeholder="Search pages or members..."
        leftSection={<IconSearch size={18} stroke={1.5} />}
        rightSection={isFetching && canSearchMembers ? <Loader size="xs" /> : null}
        rightSectionPointerEvents="none"
      />

      <Spotlight.ActionsList>
        {isEmpty && <Spotlight.Empty>Nothing found...</Spotlight.Empty>}

        {filteredNav.length > 0 &&
          (showMemberGroup ? (
            <Spotlight.ActionsGroup label="Pages">
              {filteredNav.map((item) => (
                <Spotlight.Action
                  key={item.id}
                  label={item.label}
                  description={item.description}
                  leftSection={item.icon}
                  highlightQuery
                  onClick={() => navigate(item.path)}
                />
              ))}
            </Spotlight.ActionsGroup>
          ) : (
            filteredNav.map((item) => (
              <Spotlight.Action
                key={item.id}
                label={item.label}
                description={item.description}
                leftSection={item.icon}
                highlightQuery
                onClick={() => navigate(item.path)}
              />
            ))
          ))}

        {showMemberGroup && (
          <Spotlight.ActionsGroup label="Members">
            {members.map((m) => {
              const name = m.firstNameLastInitial ?? `Member #${m.id}`;
              // Prefer guild nickname as the displayed alias; fall back to discord username.
              const alias = m.guildNickname ?? m.discordUsername ?? null;
              const meta = [ROLE_LABEL[m.role], m.websiteId ?? null, m.primaryDepartment ?? null]
                .filter(Boolean)
                .join(" · ");
              return (
                <Spotlight.Action
                  key={`member-${m.id}`}
                  onClick={() => navigate(`/members/${m.id}`)}
                >
                  <Group wrap="nowrap" w="100%" gap="sm">
                    <IconUser
                      size={18}
                      stroke={1.5}
                      style={{ color: "var(--muted)", flexShrink: 0 }}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <Group gap={6} wrap="nowrap" align="baseline">
                        <Text fw={500} truncate size="sm">
                          {name}
                        </Text>
                        {alias && (
                          <Text size="xs" c="dimmed" style={{ flexShrink: 0, fontWeight: 400 }}>
                            ({alias})
                          </Text>
                        )}
                      </Group>
                      <Text c="dimmed" size="xs" truncate>
                        {meta}
                      </Text>
                    </div>
                  </Group>
                </Spotlight.Action>
              );
            })}
          </Spotlight.ActionsGroup>
        )}
      </Spotlight.ActionsList>
    </Spotlight.Root>
  );
}
